import { useState } from 'react'
import { cn } from '../../lib/cn.js'
import { FileViewer } from '../FileViewer.js'
import { EmptyState } from '../ui.js'
import { attachmentFile, attachmentMeta } from './feed.js'
import { Section } from './panes.js'
import type { Job } from './types.js'

/**
 * Photos and paperwork on the job.
 *
 * Nothing here uploads: the whole card is the drop target (`FileDrop.tsx`), so
 * a file can be dropped from any tab rather than only once this one is open.
 *
 * A file has bytes only once the migrator's attachment phase has fetched them,
 * which during the dual run means the sync window and nothing else. Those show a
 * thumbnail; the rest keep the filename and say why there is no picture, because
 * a grey square with no explanation reads as a bug.
 *
 * Photos are a tiled grid and documents are a list, because the useful thing
 * about a photo is what is in it and the useful thing about a PDF is its name.
 * Either opens in `FileViewer` first; a new tab is one of its buttons. Photos
 * step through the photos, and documents through the documents.
 */
export function FilesPane({ job }: { job: Job }) {
  const [viewing, setViewing] = useState<{ id: string; photo: boolean } | null>(null)

  const files = job.attachments.map((attachment) => ({
    attachment,
    file: attachmentFile(attachment),
  }))

  const photos = files.filter((entry) => entry.file.isImage)
  const documents = files.filter((entry) => !entry.file.isImage)
  const waiting = files.filter((entry) => !entry.file.url).length

  if (files.length === 0) {
    return (
      <EmptyState
        title="Nothing attached yet"
        description="Drop photos or documents anywhere on the job card to attach them."
      />
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <FileViewer
        files={(viewing?.photo ? photos : documents).map((entry) => entry.file)}
        openId={viewing?.id ?? null}
        onClose={() => setViewing(null)}
      />

      {photos.length > 0 ? (
        <Section title="Photos" hint={`${photos.length}`}>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
            {photos.map(({ attachment, file }) => (
              <figure
                key={attachment.id}
                title={attachmentMeta(attachment)}
                className="overflow-hidden rounded-md ring-1 ring-inset ring-ink-200"
              >
                {file.url ? (
                  <button
                    type="button"
                    aria-label={`View ${file.name}`}
                    onClick={() => setViewing({ id: file.id, photo: true })}
                    className="block w-full"
                  >
                    <img
                      src={file.url}
                      alt={file.caption}
                      // A job can carry sixty photographs and every one is a
                      // request through the API rather than a CDN, so the grid
                      // loads what is scrolled to.
                      loading="lazy"
                      decoding="async"
                      className="aspect-[4/3] w-full bg-ink-100 object-cover"
                    />
                  </button>
                ) : (
                  <span className="flex aspect-[4/3] flex-col justify-end bg-ink-100 p-1.5">
                    <span className="truncate text-[10px] font-medium text-ink-600">
                      {file.name}
                    </span>
                  </span>
                )}
              </figure>
            ))}
          </div>
        </Section>
      ) : null}

      {documents.length > 0 ? (
        <Section title="Documents" hint={`${documents.length}`}>
          <div className="grid gap-2 sm:grid-cols-2">
            {documents.map(({ attachment, file }) => {
              const body = (
                <>
                  <span
                    className={cn(
                      'w-9 shrink-0 rounded-sm py-0.5 text-center text-[10px] font-bold',
                      'bg-red-50 text-red-700',
                    )}
                  >
                    {file.kind}
                  </span>
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate text-[13px] font-medium text-ink-900">
                      {file.name}
                    </span>
                    <span className="truncate text-xs text-ink-500">
                      {attachmentMeta(attachment)}
                    </span>
                  </span>
                </>
              )

              const className =
                'flex items-center gap-2.5 rounded-md px-2.5 py-2 ring-1 ring-inset ring-ink-200'

              return file.url ? (
                <button
                  key={attachment.id}
                  type="button"
                  onClick={() => setViewing({ id: file.id, photo: false })}
                  className={cn(className, 'text-left hover:bg-ink-50')}
                >
                  {body}
                </button>
              ) : (
                <div key={attachment.id} className={className}>
                  {body}
                </div>
              )
            })}
          </div>
        </Section>
      ) : null}

      {waiting > 0 ? (
        <p className="text-xs text-ink-500">
          {waiting === files.length
            ? 'These files are still in ServiceM8.'
            : `${waiting} of these are still in ServiceM8.`}{' '}
          They arrive when the sync fetches them.
        </p>
      ) : null}
    </div>
  )
}
